import { useMemo } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const COLORS = ["#059669", "#0ea5e9", "#8b5cf6", "#f59e0b", "#ef4444", "#ec4899", "#14b8a6", "#6366f1"];

const formatHours = (value) => `${value.toFixed(1)}h`;

export default function TimeEntryDistributionChart({ data = [], isLoading, error }) {
  const chartData = useMemo(() => {
    return data.map((p) => ({
      name: p.project_name?.length > 18 ? p.project_name.slice(0, 16) + "…" : p.project_name,
      fullName: p.project_name,
      hours: +(p.total_minutes / 60).toFixed(1),
      open: +(p.open_minutes / 60).toFixed(1),
      invoiced: +(p.invoiced_minutes / 60).toFixed(1),
    }));
  }, [data]);

  if (error) {
    return (
      <Card data-testid="time-distribution-chart">
        <CardContent className="flex items-center justify-center py-16 text-sm text-red-500">
          Fehler beim Laden der Daten
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="time-distribution-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-slate-800">
          Stunden nach Projekt
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[300px] w-full rounded-xl" />
        ) : chartData.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-sm text-slate-400">
            Keine Zeiteinträge im gewählten Zeitraum
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 44)}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 20, left: 10, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" tickFormatter={formatHours} tick={{ fontSize: 12, fill: "#475569" }} />
              <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12, fill: "#475569" }} />
              <Tooltip
                formatter={(value, name) => [formatHours(value), name === "invoiced" ? "Abgerechnet" : "Offen"]}
                labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "13px" }}
              />
              <Bar dataKey="invoiced" stackId="a" name="Abgerechnet" radius={[0, 0, 0, 0]} fill="#059669" />
              <Bar dataKey="open" stackId="a" name="Offen" radius={[0, 4, 4, 0]} fill="#94a3b8">
                {chartData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length] + "55"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
        {chartData.length > 0 && (
          <div className="flex items-center gap-4 pt-3 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-600" /> Abgerechnet
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-slate-400" /> Offen
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
